import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { getInitials } from "@/utils/strings";
import { TLiveAgent } from "@/models/call.center.model";
import { CALLCENTER_USER_STATUS_IN_CALL, CALLCENTER_USER_STATUS_NOT_AVAILABLE, CALLCENTER_USER_STATUS_OFFLINE, CALLCENTER_USER_STATUS_READY } from "@icebergeradmin/common";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

// Demo-ui shows 4 avatars in the at-work pill (CallCenterLayout.tsx)
const MAX_AVATARS_TO_SHOW = 4;

interface LiveAgentsAvatarGroupProps {
    total: number;
    liveAgents?: TLiveAgent[];
    loading?: boolean;
}

const AGENT_STATUS_LABELS = {
    [CALLCENTER_USER_STATUS_READY]: { label: "Ready", color: "text-accent-green" },
    [CALLCENTER_USER_STATUS_NOT_AVAILABLE]: { label: "Not Ready", color: "text-accent-red" },
    [CALLCENTER_USER_STATUS_IN_CALL]: { label: "In Call", color: "text-accent-amber" },
    [CALLCENTER_USER_STATUS_OFFLINE]: { label: "Offline", color: "text-slate-500" },
}

export default function LiveAgentsAvatarGroup({
    total,
    liveAgents = [],
    loading,
}: LiveAgentsAvatarGroupProps) {
    if (loading) {
        return (
            <div className="inline-flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                <div className="flex -space-x-1.5">
                    {Array.from({ length: MAX_AVATARS_TO_SHOW }).map((_, i) => (
                        <Skeleton key={i} className="h-6 w-6 rounded-full ring-2 ring-ink-900" />
                    ))}
                </div>
                <Skeleton className="h-6 w-20" />
            </div>
        );
    }

    const atWorkAgents = liveAgents.filter(
        (a) => a.status === CALLCENTER_USER_STATUS_IN_CALL || a.status === CALLCENTER_USER_STATUS_READY
    );
    const inCallCount = liveAgents.filter((a) => a.status === CALLCENTER_USER_STATUS_IN_CALL).length;
    const availableCount = liveAgents.filter((a) => a.status === CALLCENTER_USER_STATUS_READY).length;
    const agentsToShow = liveAgents.slice(0, MAX_AVATARS_TO_SHOW);

    return (
        <HoverCard openDelay={200} closeDelay={150}>
            <HoverCardTrigger asChild>
                {/* At-work counter pill ported from demo-ui CallCenterLayout.tsx */}
                <button
                    type="button"
                    aria-label="Show live agents status"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-left transition hover:border-white/[0.14] hover:bg-white/[0.04]"
                >
                    <div className="flex -space-x-1.5">
                        {agentsToShow.map((u) => (
                            <Avatar
                                key={u.id}
                                className={cn(
                                    "h-6 w-6 ring-2 ring-ink-900",
                                    u.status === CALLCENTER_USER_STATUS_IN_CALL && "ring-accent-amber/40",
                                    u.status === CALLCENTER_USER_STATUS_READY && "ring-accent-green/40"
                                )}
                            >
                                <AvatarImage src={u.avatar || ""} />
                                <AvatarFallback className="text-[10px] font-semibold">
                                    {getInitials(`${u.firstName} ${u.lastName || ""}`)}
                                </AvatarFallback>
                            </Avatar>
                        ))}
                    </div>
                    <div className="text-[11px] leading-tight">
                        <div className="font-semibold tabular-nums text-white whitespace-nowrap">
                            {atWorkAgents.length} of {total}{" "}
                            <span className="font-medium text-slate-400">at work</span>
                        </div>
                        <div className="text-[10px] text-slate-500 whitespace-nowrap">
                            {inCallCount} in call · {availableCount} available
                        </div>
                    </div>
                </button>
            </HoverCardTrigger>

            {total > 0 && <HoverCardContent
                side="bottom"
                align="start"
                className="w-64 rounded-2xl border-white/[0.08] bg-ink-800/95 p-0 shadow-card backdrop-blur-xl"
            >
                <ScrollArea className="[&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]]:max-h-72">
                    <div className="flex flex-col py-2">
                        {liveAgents.map((agent) => {
                            const fullName = `${agent.firstName} ${agent.lastName || ""}`.trim();

                            return (
                                <div
                                    key={agent.id}
                                    className="flex items-center gap-3 px-4 py-2"
                                >
                                    <Avatar className="h-10 w-10 shrink-0">
                                        <AvatarImage src={agent.avatar || ""} />
                                        <AvatarFallback>{getInitials(fullName)}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col min-w-0 overflow-hidden">
                                        <span className="text-sm text-white truncate">
                                            {fullName}
                                        </span>
                                        <span className={cn("text-xs", AGENT_STATUS_LABELS[agent.status as keyof typeof AGENT_STATUS_LABELS].color)}>
                                            {AGENT_STATUS_LABELS[agent.status as keyof typeof AGENT_STATUS_LABELS].label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>
            </HoverCardContent>
            }
        </HoverCard>
    );
}
